import { uniq } from 'lodash-es';
import { useManageParameters } from '~/common/api';
import {
  IAutocompleteParameterQuery,
  IDimensionParameterResult,
  IBaseSearchFormSchema,
} from '~/purchase/types';

const numberAscSorter = (a: number, b: number) => a - b;
const stringAscSorter = (a: string, b: string) =>
  a.charCodeAt(0) - b.charCodeAt(0);

type SuitableArgs = {
  parameters: Array<IDimensionParameterResult>;
  currentValues: IBaseSearchFormSchema;
};
function getSuitableHeights({ parameters, currentValues }: SuitableArgs) {
  const matchedWidthItem = parameters.find(
    (item) => item.width === currentValues.width,
  );
  return matchedWidthItem?.heights.map((item) => item.height) || [];
}
function getSuitableDiameters({ parameters, currentValues }: SuitableArgs) {
  const matchedWidthItem = parameters.find(
    (item) => item.width === currentValues.width,
  );
  const matchedHeightItem = matchedWidthItem?.heights.find(
    (item) => item.height === currentValues.height,
  );
  return matchedHeightItem?.diameters.map((item) => item.diameter) || [];
}
function getSuitableLoadIndexes({ parameters, currentValues }: SuitableArgs) {
  const matchedWidthItem = parameters.find(
    (item) => item.width === currentValues.width,
  );
  const matchedHeightItem = matchedWidthItem?.heights.find(
    (item) => item.height === currentValues.height,
  );
  const matchedDiameterItem = matchedHeightItem?.diameters.find(
    (item) => item.diameter === currentValues.diameter,
  );
  return matchedDiameterItem?.loadIndexes.map((item) => item.loadIndex) || [];
}
function getSuitableSpeedIndexes({ parameters, currentValues }: SuitableArgs) {
  const matchedWidthItem = parameters.find(
    (item) => item.width === currentValues.width,
  );
  const matchedHeightItem = matchedWidthItem?.heights.find(
    (item) => item.height === currentValues.height,
  );
  const matchedDiameterItem = matchedHeightItem?.diameters.find(
    (item) => item.diameter === currentValues.diameter,
  );
  const matchedLoadIndexItem = matchedDiameterItem?.loadIndexes.filter((item) =>
    currentValues.loadIndexes?.includes(item.loadIndex),
  );
  return (
    matchedLoadIndexItem?.flatMap((item) =>
      item.speedIndexes.map((speedIndexItem) => speedIndexItem.speedIndex),
    ) || []
  );
}
type Args = {
  selected: IBaseSearchFormSchema;
  dimensions: Array<IDimensionParameterResult>;
} & IAutocompleteParameterQuery;

const getDimensionAttr = ({
  dimensions,
  selected,
}: Pick<Args, 'dimensions' | 'selected'>) => {
  const widths = dimensions.map((item) => item.width) || [];
  const heights = getSuitableHeights({
    parameters: dimensions,
    currentValues: selected,
  });
  const diameters = getSuitableDiameters({
    parameters: dimensions,
    currentValues: selected,
  });
  const loadIndexes = getSuitableLoadIndexes({
    parameters: dimensions,
    currentValues: selected,
  });
  const speedIndexes = getSuitableSpeedIndexes({
    parameters: dimensions,
    currentValues: selected,
  });

  return {
    widths: widths.sort(numberAscSorter),
    heights: heights.sort(numberAscSorter),
    diameters: diameters.sort(numberAscSorter),
    loadIndexes: uniq(loadIndexes.sort(numberAscSorter)),
    speedIndexes: uniq(speedIndexes.sort(stringAscSorter)),
  };
};

// useDimensionAttr is used as react hook (can call another hooks)
// getDimensionAttr is simple pure js function that can be called inside another function in an imperative way
export const useDimensionAttr = ({
  vehicleTypes = [],
  selected,
  customerId,
}: Pick<Args, 'vehicleTypes' | 'selected'> & { customerId: string }) => {
  const { getDimensions } = useManageParameters({
    getDimensions: {
      args: {
        vehicleTypes: vehicleTypes,
        customerId: customerId,
      },
    },
  });
  return getDimensionAttr({
    selected,
    dimensions: getDimensions.data?.data || [],
  });
};
